import { Link } from "react-router";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { ArticleCard } from "./ArticleCard";
import { getCategoryTheme } from "@/lib/categoryTheme";
import type { Article } from "@/data/articles";
import type { CategoryRow } from "@/hooks/useCategories";

interface CategorySectionProps {
  readonly category: CategoryRow;
  readonly articles: Article[];
  /** Max articles to show (default 6) */
  readonly maxArticles?: number;
}

export function CategorySection({
  category,
  articles,
  maxArticles = 6,
}: CategorySectionProps) {
  const shown = articles.slice(0, maxArticles);
  if (shown.length === 0) return null;

  // Get theme for gradient and canonical icon
  const theme = getCategoryTheme(category.slug || category.name);
  const Icon = theme.Icon;

  return (
    <section className="mb-12">
      {/* Section header with colored accent icon */}
      <div className="flex items-center justify-between mb-5 gap-4">
        <div className="flex items-center gap-3 min-w-0">
          {/* Left accent icon with gradient background */}
          <span
            className="inline-flex h-8 w-8 md:h-10 md:w-10 items-center justify-center rounded-lg shrink-0 shadow-sm"
            style={{ background: theme.badgeGradient }}
          >
            <Icon className="h-4 w-4 md:h-5 md:w-5 text-white" />
          </span>
          <div className="min-w-0 flex flex-col justify-center">
            <h2 className="text-lg md:text-xl font-bold text-foreground leading-tight truncate">
              {category.name}
            </h2>
            {category.description && (
              <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">
                {category.description}
              </p>
            )}
          </div>
        </div>

        <Link
          to={`/category/${category.slug}`}
          className="flex items-center gap-1.5 text-xs sm:text-sm font-semibold shrink-0 group transition-all px-3 py-1.5 rounded-lg shadow-sm hover:shadow-md hover:scale-105"
          style={{ background: theme.badgeGradient, color: "white" }}
        >
          View All
          <ArrowRight className="h-3.5 w-3.5 transition-transform duration-200 group-hover:translate-x-0.5" />
        </Link>
      </div>

      {/* Articles grid */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.1 }}
        transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:gap-6 gap-5"
      >
        {shown.map((article) => (
          <ArticleCard key={article.id} article={article} />
        ))}
      </motion.div>
    </section>
  );
}
